import { X, Star, Trash2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCompare } from '@/contexts/CompareContext';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';

const CompareDrawer = () => {
  const { compareItems, removeFromCompare, clearCompare, isCompareOpen, setIsCompareOpen } = useCompare();

  const specs = [
    { key: 'price', label: 'Price', format: (p: any) => `$${p.price.toFixed(2)}` },
    { key: 'collection', label: 'Collection', format: (p: any) => p.collection.replace('-', ' ').toUpperCase() },
    { key: 'category', label: 'Category', format: (p: any) => p.category.charAt(0).toUpperCase() + p.category.slice(1) },
    { key: 'rating', label: 'Rating', format: (p: any) => (
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${i < p.rating ? 'fill-primary text-primary' : 'text-muted-foreground'}`}
          />
        ))}
      </div>
    )},
    { key: 'colors', label: 'Colors Available', format: (p: any) => (
      <div className="flex gap-1 flex-wrap">
        {p.colors.length > 0 ? p.colors.slice(0, 4).map((color: string, i: number) => (
          <div
            key={i}
            className="w-5 h-5 rounded-full border border-border"
            style={{ backgroundColor: color }}
          />
        )) : <span className="text-muted-foreground">N/A</span>}
        {p.colors.length > 4 && <span className="text-xs text-muted-foreground">+{p.colors.length - 4}</span>}
      </div>
    )},
    { key: 'isNew', label: 'New Arrival', format: (p: any) => p.isNew ? '✓ Yes' : '—' },
    { key: 'isBestSeller', label: 'Best Seller', format: (p: any) => p.isBestSeller ? '✓ Yes' : '—' },
  ];

  return (
    <Sheet open={isCompareOpen} onOpenChange={setIsCompareOpen}>
      <SheetContent side="bottom" className="h-[85vh] p-0">
        <SheetHeader className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <SheetTitle className="font-display text-2xl">
              Compare Products ({compareItems.length}/4)
            </SheetTitle>
            {compareItems.length > 0 && (
              <button
                onClick={clearCompare}
                className="text-sm text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
              >
                <Trash2 className="w-4 h-4" />
                Clear All
              </button>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(85vh-100px)]">
          {compareItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <p className="text-muted-foreground mb-4">No products to compare yet.</p>
              <p className="text-sm text-muted-foreground">
                Click the compare icon on product cards to add items.
              </p>
            </div>
          ) : (
            <div className="p-6">
              {/* Products Header Row */}
              <div className="grid gap-4" style={{ gridTemplateColumns: `150px repeat(${compareItems.length}, 1fr)` }}>
                <div /> {/* Empty cell for labels column */}
                {compareItems.map((product) => (
                  <div key={product.id} className="relative group">
                    <button
                      onClick={() => removeFromCompare(product.id)}
                      className="absolute -top-2 -right-2 z-10 p-1.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="bg-muted/50 rounded-xl p-4 text-center">
                      <div className="aspect-square rounded-lg overflow-hidden mb-3 bg-background">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <h3 className="font-semibold text-sm mb-1 line-clamp-2">{product.name}</h3>
                      <p className="text-xs text-muted-foreground mb-3">#{product.styleNumber}</p>
                      <Link
                        to={`/product/${product.id}`}
                        onClick={() => setIsCompareOpen(false)}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        View Details <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Specs Comparison Table */}
              <div className="mt-6 border border-border rounded-xl overflow-hidden">
                {specs.map((spec, index) => (
                  <div
                    key={spec.key}
                    className={`grid gap-4 ${index % 2 === 0 ? 'bg-muted/30' : 'bg-background'}`}
                    style={{ gridTemplateColumns: `150px repeat(${compareItems.length}, 1fr)` }}
                  >
                    <div className="p-4 font-medium text-sm text-foreground border-r border-border">
                      {spec.label}
                    </div>
                    {compareItems.map((product) => (
                      <div key={product.id} className="p-4 text-sm text-foreground">
                        {spec.format(product)}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Description Row */}
              <div className="mt-6 border border-border rounded-xl overflow-hidden">
                <div
                  className="grid gap-4 bg-muted/30"
                  style={{ gridTemplateColumns: `150px repeat(${compareItems.length}, 1fr)` }}
                >
                  <div className="p-4 font-medium text-sm text-foreground border-r border-border">
                    Description
                  </div>
                  {compareItems.map((product) => (
                    <div key={product.id} className="p-4 text-sm text-muted-foreground">
                      {product.description}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default CompareDrawer;
